import React, { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_URL, patientProfileUrl, therapistActivityPlanUrl, caregiverSupportUrl } from "../../config/constants";
import socket from "../../socket";
import AddPatientModal from "../../components/addPatientModal";
import PatientFilterModal from "../../components/patientFilterModal";
import AppSidebar from "../../components/AppSidebar";
import avatarMap from "../../assets/avatars";
import therapistAvatar from "../../assets/therapist.png";
import {
  Layout, Main, TopBar, TopBarInner, AddButton, SearchWrapper, SearchIcon, SearchInput,
  UserBox, UserName, UserAvatar,
  Content, TitleRow, PageTitle, FilterButton,
  Table, Thead, Th, Tbody, Tr, Td, AvatarCell, EmptyRow, TherapistName,
  ActionCell, IconBtn, NotifBadge,
} from "./styles";

const COHORTS = [1, 2];
const SEXES = ["Male", "Female"];

// Tooltip nativo con el texto completo, solo si la celda está recortada.
const showIfTruncated = (e) => {
  const el = e.currentTarget;
  el.title = el.scrollWidth > el.clientWidth ? el.textContent : "";
};

const calcAgeMonths = (patient) => {
  if (patient.age_at_start_corrected) return patient.age_at_start_corrected;
  const baseDate = patient.is_premature
    ? patient.expected_delivery_date
    : patient.date_of_birth;
  if (!baseDate || !patient.treatment_start_date) return "—";
  const base = new Date(baseDate);
  const start = new Date(patient.treatment_start_date);
  const months =
    (start.getFullYear() - base.getFullYear()) * 12 +
    (start.getMonth() - base.getMonth());
  return months > 0 ? `${months} months` : "—";
};

// Corrected age in whole months, from the same base date the table displays.
const ageInMonths = (patient) => {
  const baseDate = patient.is_premature
    ? patient.expected_delivery_date
    : patient.date_of_birth;
  if (!baseDate || !patient.treatment_start_date) return null;
  const base = new Date(baseDate);
  const start = new Date(patient.treatment_start_date);
  if (isNaN(base.getTime()) || isNaN(start.getTime())) return null;
  let months =
    (start.getFullYear() - base.getFullYear()) * 12 +
    (start.getMonth() - base.getMonth());
  if (start.getDate() < base.getDate()) months--;
  return months < 0 ? null : months;
};

const IconFilter = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
  </svg>
);

const IconProfile = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const IconActivityPlan = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const IconSummary = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);
const IconCaregiverSupport = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const TherapistHome = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const token = localStorage.getItem("token");

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  const [patients, setPatients] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [pathologies, setPathologies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState(null); // null = no filter applied

  const fetchPatients = () => {
    fetch(`${API_URL}/patients`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => { setPatients(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchPatients();

    const headers = { Authorization: `Bearer ${token}` };
    fetch(`${API_URL}/hospitals/therapists`, { headers })
      .then((r) => r.json())
      .then((d) => setTherapists(Array.isArray(d) ? d : []))
      .catch(() => {});
    fetch(`${API_URL}/pathologies`, { headers })
      .then((r) => r.json())
      .then((d) => setPathologies(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Un mensaje entrante sube el contador de esa fila sin recargar la lista.
  useEffect(() => {
    const onMessage = (msg) => {
      if (msg.receiver_id !== currentUser.id) return;
      setPatients((prev) =>
        prev.map((p) =>
          p.id === msg.sender_id ? { ...p, unread_count: (p.unread_count || 0) + 1 } : p
        )
      );
    };
    socket.on("receiveMessage", onMessage);
    return () => socket.off("receiveMessage", onMessage);
  }, [currentUser.id]);

  // Defaults = everything selected, no age bounds.
  const defaultFilters = useMemo(() => ({
    therapistIds: therapists.map((th) => th.id),
    cohorts: COHORTS,
    sexes: SEXES,
    ageMin: "",
    ageMax: "",
    pathologyIds: pathologies.map((p) => p.id),
  }), [therapists, pathologies]);

  const filtered = useMemo(() => {
    // A group with every option selected is treated as "no filter", so patients
    // with no therapist / cohort / sex / diagnosis are not silently dropped.
    const inGroup = (selected, all, value) =>
      selected.length === all.length || (value != null && selected.includes(value));

    const q = search.trim().toLowerCase();

    return patients.filter((p) => {
      if (q) {
        const matches =
          p.patient_code?.toLowerCase().includes(q) ||
          p.username?.toLowerCase().includes(q) ||
          p.pathology?.toLowerCase().includes(q) ||
          p.assigned_therapist_username?.toLowerCase().includes(q);
        if (!matches) return false;
      }

      if (!filters) return true;

      if (!inGroup(filters.therapistIds, defaultFilters.therapistIds, p.assigned_therapist_id)) return false;
      if (!inGroup(filters.cohorts, COHORTS, p.cohort)) return false;
      if (!inGroup(filters.sexes, SEXES, p.sex)) return false;
      if (!inGroup(filters.pathologyIds, defaultFilters.pathologyIds, p.pathology_id)) return false;

      const min = filters.ageMin === "" ? null : Number(filters.ageMin);
      const max = filters.ageMax === "" ? null : Number(filters.ageMax);
      if (min !== null || max !== null) {
        const age = ageInMonths(p);
        if (age === null) return false;
        if (min !== null && age < min) return false;
        if (max !== null && age > max) return false;
      }

      return true;
    });
  }, [patients, search, filters, defaultFilters]);

  const handleProfile = (patient, e) => {
    e.stopPropagation();
    history.push(patientProfileUrl(patient.id), { patient });
  };

  const handleActivityPlan = (patient, e) => {
    e.stopPropagation();
    history.push(therapistActivityPlanUrl(patient.id), { patient });
  };

  const handleCaregiverSupport = (patient, e) => {
    e.stopPropagation();
    if (patient.assigned_therapist_id !== currentUser.id) return;
    history.push(caregiverSupportUrl(patient.id), { patient });
  };

  return (
    <Layout>
      <AppSidebar patient={selectedPatient} activeNav="patients" />

      <Main>
        <TopBar>
          <TopBarInner>
            <AddButton onClick={() => setShowAddModal(true)}>
              {t("patients.addNew")}
            </AddButton>
            <SearchWrapper>
              <SearchIcon>🔍</SearchIcon>
              <SearchInput
                placeholder={t("patients.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchWrapper>
          </TopBarInner>

          <UserBox>
            <UserName>{currentUser.username || "—"}</UserName>
            <UserAvatar src={therapistAvatar} alt={currentUser.username || "therapist"} />
          </UserBox>
        </TopBar>

        <Content>
          <TitleRow>
            <PageTitle>{t("patients.title")}</PageTitle>
            <FilterButton
              $active={!!filters}
              title={t("filter.title")}
              onClick={() => setShowFilterModal(true)}
            >
              <IconFilter />
            </FilterButton>
          </TitleRow>
          <Table>
            <Thead>
              <tr>
                <Th style={{ width: 52 }} />
                <Th style={{ width: 200 }}>{t("patients.table.patientId")}</Th>
                <Th style={{ width: 200 }}>{t("patients.table.therapist")}</Th>
                <Th style={{ width: 80 }}>{t("patients.table.cohort")}</Th>
                <Th style={{ width: 200 }}>{t("patients.table.age")}</Th>
                <Th style={{ width: 100 }}>{t("patients.table.sex")}</Th>
                <Th style={{ width: 200 }}>{t("patients.table.pathology")}</Th>
                <Th>{t("patients.table.actions")}</Th>
              </tr>
            </Thead>
            <Tbody>
              {loading ? (
                <EmptyRow><td colSpan={8}>{t("patients.loading")}</td></EmptyRow>
              ) : filtered.length === 0 ? (
                <EmptyRow><td colSpan={8}>{t("patients.empty")}</td></EmptyRow>
              ) : (
                filtered.map((p) => (
                  <Tr
                    key={p.id}
                    $selected={selectedPatient?.id === p.id}
                    onClick={() => setSelectedPatient(p)}
                  >
                    <Td>
                      <AvatarCell
                        src={avatarMap[p.avatar_url] || Object.values(avatarMap)[0]}
                        alt="avatar"
                      />
                    </Td>
                    <Td onMouseEnter={showIfTruncated}>{p.patient_code || "—"}</Td>
                    <Td onMouseEnter={showIfTruncated}>
                      <TherapistName $me={p.assigned_therapist_id === currentUser.id}>
                        {p.assigned_therapist_username || "—"}
                      </TherapistName>
                    </Td>
                    <Td>{p.cohort ?? "—"}</Td>
                    <Td onMouseEnter={showIfTruncated}>{calcAgeMonths(p)}</Td>
                    <Td onMouseEnter={showIfTruncated}>{p.sex || "—"}</Td>
                    <Td onMouseEnter={showIfTruncated}>{p.pathology || "—"}</Td>
                    <Td>
                      <ActionCell>
                        <IconBtn title={t("nav.profile")} onClick={(e) => handleProfile(p, e)}>
                          <IconProfile />
                        </IconBtn>
                        <IconBtn title={t("nav.activityPlan")} onClick={(e) => handleActivityPlan(p, e)}>
                          <IconActivityPlan />
                        </IconBtn>
                        <IconBtn title={t("nav.summary")} onClick={(e) => e.stopPropagation()}>
                          <IconSummary />
                        </IconBtn>
                        <IconBtn
                          title={
                            p.assigned_therapist_id === currentUser.id
                              ? t("nav.caregiverSupport")
                              : t("caregiver.notAssigned")
                          }
                          disabled={p.assigned_therapist_id !== currentUser.id}
                          onClick={(e) => handleCaregiverSupport(p, e)}
                        >
                          <IconCaregiverSupport />
                          {p.unread_count > 0 && (
                            <NotifBadge>{p.unread_count > 99 ? "99+" : p.unread_count}</NotifBadge>
                          )}
                        </IconBtn>
                      </ActionCell>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Content>
      </Main>

      {showAddModal && (
        <AddPatientModal
          onClose={() => setShowAddModal(false)}
          onCreated={() => { setShowAddModal(false); fetchPatients(); }}
        />
      )}

      {showFilterModal && (
        <PatientFilterModal
          filters={filters}
          defaultFilters={defaultFilters}
          therapists={therapists}
          pathologies={pathologies}
          cohorts={COHORTS}
          sexes={SEXES}
          currentUserId={currentUser.id}
          onApply={(next) => {
            const isDefault =
              next.ageMin === "" && next.ageMax === "" &&
              next.therapistIds.length === defaultFilters.therapistIds.length &&
              next.cohorts.length === COHORTS.length &&
              next.sexes.length === SEXES.length &&
              next.pathologyIds.length === defaultFilters.pathologyIds.length;
            setFilters(isDefault ? null : next);
            setShowFilterModal(false);
          }}
          onClose={() => setShowFilterModal(false)}
        />
      )}
    </Layout>
  );
};

export default TherapistHome;
